import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'

import { I2070Form } from 'clickNgo/models/crud/forms'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'

import {
  FlagRecord,
  FlagRecordDependency,
  FlagRecordDependencyMarket,
  FlagRecordDependencyType,
  FlagRecordEditableFields,
  getReverseFlagRecordDependencyType,
  isToolFlagRecordType,
} from '../model/types'

const FLAG_DEPENDENCY_FORM_ID = 2070
const EPI_DEPENDENCY_TYPE: FlagRecordDependencyType = 'EPI'
const USER_RIGHTS_DEPENDENCY_TYPE: FlagRecordDependencyType = 'UserRights'

type FlagDependencyRecordDraft = {
  flagCode: string
  market: string
  toolsFlagCode: string
  type: FlagRecordDependencyType
}

type FlagDependencyFormPayload = Pick<I2070Form, 'FlagCode' | 'ToolsFlagCode' | 'Type'> & {
  Market: string
}

type FlagDependencyFormUpdatePayload = FlagDependencyFormPayload & {
  EntryId: string
}

const normalizeText = (value?: string | number | Date | null): string => {
  return value === undefined || value === null ? '' : String(value).trim()
}

const getDependencyRelationKey = (
  dependency: Pick<FlagDependencyRecordDraft, 'flagCode' | 'toolsFlagCode' | 'type'>
) => `${dependency.flagCode}\u0000${dependency.toolsFlagCode}\u0000${dependency.type}`

const getExistingDependencyRelationKey = (dependency: FlagRecordDependency) =>
  getDependencyRelationKey({
    flagCode: normalizeText(dependency.flagCode),
    toolsFlagCode: normalizeText(dependency.toolsFlagCode),
    type: dependency.type,
  })

const getDependencyMarketKey = (market: string) => normalizeText(market)

const groupByRelationKey = <T>(
  records: T[],
  getRelationKey: (record: T) => string
): Map<string, T[]> => {
  const groups = new Map<string, T[]>()

  records.forEach((record) => {
    const key = getRelationKey(record)
    const group = groups.get(key)

    if (group) {
      group.push(record)
      return
    }

    groups.set(key, [record])
  })

  return groups
}

const mapDependencyRecords = (
  toolsFlagCode: string,
  dependencies: FlagRecordDependencyMarket[],
  type: FlagRecordDependencyType
): FlagDependencyRecordDraft[] => {
  const normalizedToolsFlagCode = normalizeText(toolsFlagCode)

  if (!normalizedToolsFlagCode) {
    return []
  }

  return dependencies
    .map((dependency) => ({
      flagCode: normalizeText(dependency.code),
      market: normalizeText(dependency.market),
      toolsFlagCode: normalizedToolsFlagCode,
      type,
    }))
    .filter((dependency) => dependency.flagCode)
}

const mapToolDependencyRecords = (
  flagCode: string,
  dependencies: FlagRecordDependencyMarket[],
  type: FlagRecordDependencyType
): FlagDependencyRecordDraft[] => {
  const normalizedFlagCode = normalizeText(flagCode)

  if (!normalizedFlagCode) {
    return []
  }

  return dependencies
    .map((dependency) => ({
      flagCode: normalizedFlagCode,
      market: normalizeText(dependency.market),
      toolsFlagCode: normalizeText(dependency.code),
      type,
    }))
    .filter((dependency) => dependency.toolsFlagCode)
}

const map2070FormFromFlagDependency = (
  dependency: FlagDependencyRecordDraft
): FlagDependencyFormPayload => ({
  FlagCode: dependency.flagCode,
  Market: dependency.market,
  ToolsFlagCode: dependency.toolsFlagCode,
  Type: dependency.type,
})

const map2070UpdateFormFromFlagDependency = (
  entryId: string,
  dependency: FlagDependencyRecordDraft
): FlagDependencyFormUpdatePayload => ({
  ...map2070FormFromFlagDependency(dependency),
  EntryId: entryId,
})

export const getFlagDependencyRecordDrafts = (
  flagCode: string,
  draft: FlagRecordEditableFields
) => {
  if (isToolFlagRecordType(draft.type)) {
    return [
      ...mapDependencyRecords(
        flagCode,
        draft.dependentUserRightRecords,
        USER_RIGHTS_DEPENDENCY_TYPE
      ),
      ...mapDependencyRecords(flagCode, draft.dependentEpiFlagRecords, EPI_DEPENDENCY_TYPE),
    ]
  }

  const reverseDependencyType = getReverseFlagRecordDependencyType(draft.type)

  return reverseDependencyType
    ? mapToolDependencyRecords(flagCode, draft.dependentToolRecords, reverseDependencyType)
    : []
}

export const hasMissingFlagDependencyRecordMarkets = (draft: FlagRecordEditableFields) =>
  getFlagDependencyRecordDrafts(draft.code || '__pending_flag__', draft).some(
    (dependency) => dependency.flagCode && dependency.toolsFlagCode && !dependency.market
  )

const getEditableFlagDependencyRecords = (
  record: FlagRecord,
  dependencies: FlagRecordDependency[]
) => {
  const code = normalizeText(record.code)

  if (!code) {
    return []
  }

  if (isToolFlagRecordType(record.type)) {
    return dependencies.filter((dependency) => normalizeText(dependency.toolsFlagCode) === code)
  }

  const reverseDependencyType = getReverseFlagRecordDependencyType(record.type)

  return reverseDependencyType
    ? dependencies.filter(
        (dependency) =>
          normalizeText(dependency.flagCode) === code && dependency.type === reverseDependencyType
      )
    : []
}

const getRequiredDependencyEntryId = (dependency: FlagRecordDependency) => {
  const entryId = normalizeText(dependency.entryId)

  if (!entryId) {
    throw new Error('Dependency record entry id is required for update.')
  }

  return entryId
}

const getFlagDependencySyncOperations = (
  existingDependencies: FlagRecordDependency[],
  targetDependencies: FlagDependencyRecordDraft[]
) => {
  const existingByRelation = groupByRelationKey(
    existingDependencies,
    getExistingDependencyRelationKey
  )
  const targetByRelation = groupByRelationKey(targetDependencies, getDependencyRelationKey)
  const relationKeys = new Set([...existingByRelation.keys(), ...targetByRelation.keys()])
  const createPayloads: FlagDependencyFormPayload[] = []
  const updatePayloads: FlagDependencyFormUpdatePayload[] = []
  const deleteEntryIds: string[] = []

  relationKeys.forEach((relationKey) => {
    const unmatchedExisting = [...(existingByRelation.get(relationKey) || [])]
    const unmatchedTargets: FlagDependencyRecordDraft[] = []
    const targetDependencies = targetByRelation.get(relationKey) || []

    targetDependencies.forEach((targetDependency) => {
      const existingIndex = unmatchedExisting.findIndex(
        (dependency) =>
          getDependencyMarketKey(dependency.market) ===
          getDependencyMarketKey(targetDependency.market)
      )

      if (existingIndex >= 0) {
        unmatchedExisting.splice(existingIndex, 1)
        return
      }

      unmatchedTargets.push(targetDependency)
    })

    const updateCount = Math.min(unmatchedExisting.length, unmatchedTargets.length)

    for (let index = 0; index < updateCount; index += 1) {
      updatePayloads.push(
        map2070UpdateFormFromFlagDependency(
          getRequiredDependencyEntryId(unmatchedExisting[index]),
          unmatchedTargets[index]
        )
      )
    }

    unmatchedExisting
      .slice(updateCount)
      .forEach((dependency) => deleteEntryIds.push(getRequiredDependencyEntryId(dependency)))
    unmatchedTargets
      .slice(updateCount)
      .forEach((dependency) => createPayloads.push(map2070FormFromFlagDependency(dependency)))
  })

  return {
    createPayloads,
    deleteEntryIds,
    updatePayloads,
  }
}

export const createFlagDependencyRecords = async (
  flagCode: string,
  draft: FlagRecordEditableFields
) => {
  const dependencies = getFlagDependencyRecordDrafts(flagCode, draft)

  if (!dependencies.length) {
    return []
  }

  if (dependencies.some((dependency) => !dependency.market)) {
    throw new Error('Market is required for each flag dependency record.')
  }

  const source = CrudStore.get<I2070Form>(CrudStoreItem.ToolsCodeRules)
  const payload = dependencies.map(map2070FormFromFlagDependency)

  return source.create(FLAG_DEPENDENCY_FORM_ID, payload).catch((error) => {
    Logger.error('Error creating flag dependency records:', error)
    throw error
  })
}

export const syncFlagDependencyRecords = async (
  previousRecord: FlagRecord,
  draft: FlagRecordEditableFields,
  dependencyRecords: FlagRecordDependency[]
) => {
  const targetDependencies = getFlagDependencyRecordDrafts(draft.code, draft)
  const existingDependencies = getEditableFlagDependencyRecords(previousRecord, dependencyRecords)

  if (!targetDependencies.length && !existingDependencies.length) {
    return
  }

  if (targetDependencies.some((dependency) => !dependency.market)) {
    throw new Error('Market is required for each flag dependency record.')
  }

  const source = CrudStore.get<I2070Form>(CrudStoreItem.ToolsCodeRules)
  const { createPayloads, deleteEntryIds, updatePayloads } = getFlagDependencySyncOperations(
    existingDependencies,
    targetDependencies
  )

  try {
    if (updatePayloads.length) {
      await source.update(FLAG_DEPENDENCY_FORM_ID, updatePayloads)
    }

    if (createPayloads.length) {
      await source.create(FLAG_DEPENDENCY_FORM_ID, createPayloads)
    }

    for (const entryId of deleteEntryIds) {
      await source.delete(FLAG_DEPENDENCY_FORM_ID, { EntryId: entryId })
    }
  } catch (error) {
    Logger.error('Error syncing flag dependency records:', error)
    throw error
  }
}
